import { useTranslation } from 'react-i18next';
import { t as translate } from 'i18n';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import useAuthStore, { selectPrincipal } from 'stores/authStore';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Fade,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useTokens } from 'theme';
import {
  assistantErrorCode,
  assistantKeys,
  FINISHED_RUN_STATUSES,
  isAssistantId,
  isAssistantNotFound,
  markRunObservation,
  refreshConversationHistory,
  useAssistantStatus,
  useAssistantSkills,
  useActiveRun,
  useCancelRun,
  useConversation,
  useCreateConversation,
  useRunEvents,
  useSendMessage,
} from 'api/assistant';
import ConversationList from './ConversationList';
import Composer from './Composer';
import SettingsDialog from './SettingsDialog';
import { AssistantMessage, PendingAnswer, UserBubble } from './Messages';
import { inheritedFilters, initialDates } from './DashboardView';
import { SAMPLE_QUESTIONS, sendErrorText } from './copy';

const LOST_RUN_TEXT = () => translate('assistant.assistantPageConnectionToTheAnswerWasLostChecking');
// A run that failed before writing any answer (the worker never took it).
const UNANSWERED_TEXT = () => translate('assistant.assistantPageNoAnswerReceivedPleaseTryAgain');
const EMPTY_MESSAGES = [];
const BOTTOM_THRESHOLD = 64;
const JUMP_IDLE_MS = 2500;
const JUMP_FADE_MS = 250;

function Header({ status, onSettings }) {
  const { t: translate } = useTranslation();
  const t = useTokens();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
      <ChatBubbleOutlineIcon sx={{ color: t.link }} />
      <Typography variant="h5" fontWeight={800}>{translate('assistant.assistantPageAssistant')}</Typography>
      <Box sx={{ flexGrow: 1 }} />
      {status?.provider && (
        <Typography variant="body2" sx={{ color: t.inkMuted }}>
          {status.provider.label}
        </Typography>
      )}
      <Tooltip title={translate('assistant.assistantPageProjectKnowledgeBase')}>
        <IconButton component={RouterLink} to="/console/assistant/knowledge" aria-label={translate('assistant.assistantPageKnowledgeBase')}>
          <MenuBookOutlinedIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title={translate('assistant.assistantPageAssistantSettings')}>
        <IconButton onClick={onSettings} aria-label={translate('assistant.assistantPageAssistantSettings')}>
          <TuneIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

function EmptyState({ disabled, onAsk }) {
  const { t: translate } = useTranslation();
  const t = useTokens();
  return (
    <Box sx={{ m: 'auto', maxWidth: 520, textAlign: 'center', py: 6 }}>
      <ChatBubbleOutlineIcon sx={{ fontSize: 40, color: t.link, mb: 1 }} />
      <Typography variant="h6" sx={{ mb: 0.5 }}>{translate('assistant.assistantPageAskAboutYourData')}</Typography>
      <Typography variant="body2" sx={{ color: t.inkMuted, mb: 2.5 }}>{translate('assistant.assistantPageTheAssistantQueriesYourCubeDataModel')}</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
        {SAMPLE_QUESTIONS().map((q) => (
          <Chip key={q} label={q} variant="outlined" color="primary" clickable={!disabled} disabled={disabled} onClick={() => onAsk(q)} />
        ))}
      </Box>
    </Box>
  );
}

const StoredMessage = React.memo(function StoredMessage({ message, question, interactive, superseded, answered, filterValues, onFiltersChange, onSend, onReply, replySource, onJumpToMessage }) {
  useTranslation();
  const changeFilters = useCallback((values) => onFiltersChange(message.id, values), [message.id, onFiltersChange]);
  if (message.role === 'user') return (
    <>
      <UserBubble message={message} content={message.content} onReply={onReply} replySource={replySource} onJumpToMessage={onJumpToMessage} />
      {message.run?.status === 'failed' && !answered && (
        <Alert severity="warning" variant="outlined" sx={{ mb: 2.5, maxWidth: 860 }}>
          {UNANSWERED_TEXT()}
        </Alert>
      )}
    </>
  );
  return <AssistantMessage message={message} question={question} interactive={interactive} onSend={onSend} onReply={onReply} superseded={superseded} filterValues={filterValues} onFiltersChange={changeFilters} />;
});

const Thread = React.memo(function Thread({ messages, pending, live, settled, onSend, onReply, history, children }) {
  const { t: translate } = useTranslation();
  const loadLatest = history?.loadLatest;
  const loadAround = history?.loadAround;
  const scrollRef = useRef(null);
  const contentRef = useRef(null);
  const followBottom = useRef(true);
  const anchor = useRef(null);
  const reveal = useRef(null);
  const paging = useRef(false);
  const [historyError, setHistoryError] = useState('');
  const lastScrollTop = useRef(0);
  const [showJump, setShowJump] = useState(false);
  const [jumpVisible, setJumpVisible] = useState(false);
  const jumpIdleTimer = useRef(null);
  const clearJumpTimer = useCallback(() => {
    clearTimeout(jumpIdleTimer.current);
    jumpIdleTimer.current = null;
  }, []);
  const updateJump = useCallback((away) => {
    clearJumpTimer();
    setShowJump(away);
    setJumpVisible(away);
    if (away) {
      jumpIdleTimer.current = setTimeout(() => {
        jumpIdleTimer.current = null;
        setJumpVisible(false);
      }, JUMP_IDLE_MS);
    }
  }, [clearJumpTimer]);
  useEffect(() => clearJumpTimer, [clearJumpTimer]);
  const jumpToBottom = useCallback(async () => {
    if (history?.data?.next_after && loadLatest) {
      try {
        followBottom.current = true;
        await loadLatest();
      } catch (error) { setHistoryError(translate('assistant.assistantPageCouldNotLoadRecentMessagesPleaseTry')); return; }
    }
    const scroller = scrollRef.current;
    if (!scroller) return;
    followBottom.current = true;
    // scrollIntoView also moves the page and the console's outer containers.
    const bottom = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
    if (scroller.scrollTop !== bottom) scroller.scrollTop = bottom;
    lastScrollTop.current = scroller.scrollTop;
    updateJump(false);
  }, [updateJump, history?.data?.next_after, loadLatest, translate]);
  const keepPosition = useCallback(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    // A historical window has a later page: reaching its edge must not jump
    // over unread history. Latest navigation is an explicit user action.
    if (followBottom.current && !history?.data?.next_after) {
      const bottom = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
      if (scroller.scrollTop !== bottom) scroller.scrollTop = bottom;
      lastScrollTop.current = scroller.scrollTop;
      updateJump(false);
    }
    else if (scroller.scrollHeight - scroller.clientHeight - scroller.scrollTop > BOTTOM_THRESHOLD) {
      // A streamed answer can grow while the user reads. It must not wake
      // the button or reset its idle deadline.
      setShowJump(true);
    } else updateJump(false);
  }, [history?.data?.next_after, updateJump]);
  const captureAnchor = useCallback(() => {
    const scroller = scrollRef.current;
    const top = scroller.getBoundingClientRect().top;
    const nodes = Array.from(contentRef.current?.querySelectorAll('[data-message-id]') || []);
    const target = nodes.find((node) => node.getBoundingClientRect().bottom > top) || nodes[0];
    anchor.current = target ? {
      id: target.dataset.messageId, offset: target.getBoundingClientRect().top - top,
      height: scroller.scrollHeight, scrollTop: scroller.scrollTop,
    } : null;
  }, []);
  const loadPage = useCallback(async (direction) => {
    const cursor = direction === 'older' ? history?.data?.next_before : history?.data?.next_after;
    const load = direction === 'older' ? history?.loadOlder : history?.loadNewer;
    if (!cursor || !load || paging.current || history?.isFetching) return;
    paging.current = true;
    followBottom.current = false;
    captureAnchor();
    setHistoryError('');
    try { await load({ throwOnError: true, cancelRefetch: false }); }
    catch (error) {
      anchor.current = null;
      if (error?.code !== 'ERR_CANCELED' && error?.name !== 'AbortError') setHistoryError(translate('assistant.assistantPageCouldNotLoadMessagesPleaseTryAgain'));
    }
    finally { paging.current = false; }
  }, [history?.data?.next_before, history?.data?.next_after, history?.loadOlder, history?.loadNewer, history?.isFetching, captureAnchor, translate]);
  const onScroll = useCallback(() => {
    const scroller = scrollRef.current;
    const atBottom = scroller.scrollHeight - scroller.clientHeight - scroller.scrollTop <= BOTTOM_THRESHOLD;
    // Even a small upward move expresses an intent to read earlier content.
    followBottom.current = atBottom && scroller.scrollTop >= lastScrollTop.current;
    lastScrollTop.current = scroller.scrollTop;
    updateJump(!atBottom);
    if (scroller.scrollTop < 160 && !followBottom.current) loadPage('older');
    else if (atBottom && history?.data?.next_after) loadPage('newer');
  }, [updateJump, loadPage, history?.data?.next_after]);
  const revealMessage = useCallback((id, source) => {
    const scroller = scrollRef.current;
    const target = Array.from(contentRef.current?.querySelectorAll('[data-message-id]') || [])
      .find((node) => node.dataset.messageId === id);
    if (!scroller || !target) return false;
    followBottom.current = false;
    const block = source && Array.from(target.querySelectorAll('[data-quote-source]')).find((node) => node.dataset.quoteSource === JSON.stringify(source));
    const widget = ['widget', 'widget_title'].includes(source?.kind) && target.querySelector(`[data-widget-index="${Number(source.widget_index)}"]`);
    const destination = block || widget || target;
    scroller.scrollTop = Math.max(0, scroller.scrollTop + destination.getBoundingClientRect().top - scroller.getBoundingClientRect().top - 8);
    lastScrollTop.current = scroller.scrollTop;
    updateJump(scroller.scrollHeight - scroller.clientHeight - scroller.scrollTop > BOTTOM_THRESHOLD);
    target.focus({ preventScroll: true });
    target.dispatchEvent(new CustomEvent('assistant:reveal-message', { bubbles: true, detail: { messageId: id, source } }));
    return true;
  }, [updateJump]);
  useEffect(() => {
    // A collapsed dashboard or diagnostic block may appear only after its
    // disclosure request resolves. Reposition once that exact source exists.
    const revealed = (event) => {
      if (event.target !== window || !event.detail?.source) return;
      const { messageId, source } = event.detail;
      const target = Array.from(contentRef.current?.querySelectorAll('[data-message-id]') || []).find((node) => node.dataset.messageId === messageId);
      const block = target && (Array.from(target.querySelectorAll('[data-quote-source]')).find((node) => node.dataset.quoteSource === JSON.stringify(source))
        || (['widget', 'widget_title'].includes(source.kind) && target.querySelector(`[data-widget-index="${Number(source.widget_index)}"]`)));
      const scroller = scrollRef.current;
      if (!scroller || !block) return;
      followBottom.current = false;
      scroller.scrollTop = Math.max(0, scroller.scrollTop + block.getBoundingClientRect().top - scroller.getBoundingClientRect().top - 8);
      lastScrollTop.current = scroller.scrollTop;
    };
    window.addEventListener('assistant:reveal-message', revealed);
    return () => window.removeEventListener('assistant:reveal-message', revealed);
  }, []);
  const jumpToMessage = useCallback(async (id, source) => {
    if (revealMessage(id, source) || !loadAround) return;
    followBottom.current = false;
    anchor.current = null;
    setHistoryError('');
    const requested = { id, source };
    reveal.current = requested;
    try { await loadAround(id); }
    catch (error) {
      if (reveal.current !== requested) return;
      reveal.current = null;
      if (error?.code !== 'ERR_CANCELED' && error?.name !== 'AbortError') setHistoryError(isAssistantNotFound(error) ? translate('assistant.assistantPageTheOriginalMessageIsUnavailable') : translate('assistant.assistantPageCouldNotLoadTheOriginalMessagePlease'));
    }
  }, [loadAround, revealMessage, translate]);
  const [filterValues, setFilterValues] = useState({});
  const changeFilters = useCallback((id, values) => setFilterValues((current) => ({ ...current, [id]: values })), []);
  const previousFilterStates = useRef(new Map());
  const filterStates = useMemo(() => {
    const loaded = new Set(messages.map((message) => message.id));
    const states = new Map([...previousFilterStates.current].filter(([id]) => loaded.has(id)));
    for (const message of messages) {
      const dashboard = message.content?.dashboard;
      if (!states.has(message.id) && dashboard?.schema_version === 1) {
        // Keep only bounded filter metadata when a prior board is folded;
        // its widgets and saved queries remain outside this snapshot.
        states.set(message.id, { dates: initialDates(dashboard), filters: dashboard.filters || [] });
      }
    }
    previousFilterStates.current = states;
    return states;
  }, [messages]);
  // Capture the source's applied filters once when a revision arrives.
  const dashboardValues = useMemo(() => {
    const loaded = new Set(messages.map((message) => message.id));
    const kept = Object.entries(filterValues).filter(([id]) => loaded.has(id));
    let result = kept.length === Object.keys(filterValues).length ? filterValues : Object.fromEntries(kept);
    for (const message of messages) {
      if (message.content?.dashboard?.schema_version !== 1 || message.id in result) continue;
      const sourceId = message.content.dashboard_revision_of;
      const values = inheritedFilters(
        messages.find((m) => m.id === sourceId)?.content?.dashboard,
        message.content.dashboard, result[sourceId] || {}, filterStates.get(sourceId),
      );
      result = { ...result, [message.id]: values };
    }
    return result;
  }, [messages, filterValues, filterStates]);
  useEffect(() => {
    if (dashboardValues !== filterValues) setFilterValues(dashboardValues);
  }, [dashboardValues, filterValues]);
  // A step clears the streamed text, so a sum could stand still as both move.
  const liveSize = pending ? `${live.steps.length}:${live.text.length}:${live.diagnostics?.length || 0}` : '';
  useLayoutEffect(() => {
    if (reveal.current) {
      const { id, source } = reveal.current;
      if (revealMessage(id, source)) reveal.current = null;
      return;
    }
    if (anchor.current) {
      const saved = anchor.current;
      anchor.current = null;
      const scroller = scrollRef.current;
      const target = Array.from(contentRef.current?.querySelectorAll('[data-message-id]') || [])
        .find((node) => node.dataset.messageId === saved.id);
      if (target) scroller.scrollTop += target.getBoundingClientRect().top - scroller.getBoundingClientRect().top - saved.offset;
      else scroller.scrollTop = saved.scrollTop + Math.max(0, scroller.scrollHeight - saved.height);
      lastScrollTop.current = scroller.scrollTop;
      return;
    }
    keepPosition();
  }, [messages, liveSize, keepPosition, revealMessage]);
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return undefined;
    // Charts, tables and expanded answers can change height after the stream.
    // Defer writes until after ResizeObserver delivery to avoid a layout loop.
    let frame = null;
    let closed = false;
    const observer = new ResizeObserver(() => {
      if (closed || frame !== null) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        if (!closed) keepPosition();
      });
    });
    observer.observe(contentRef.current);
    observer.observe(scrollRef.current);
    return () => {
      closed = true;
      observer.disconnect();
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [keepPosition]);

  const lastIndex = messages.length - 1;
  const messageById = useMemo(() => new Map(messages.map((message) => [message.id, message])), [messages]);
  // Runs that ended with an assistant message (an answer or an apology).
  const answered = useMemo(() => new Set(messages.filter((m) => m.role === 'assistant' && m.run).map((m) => m.run.id)), [messages]);
  const superseded = useMemo(() => new Set(messages
    .flatMap((m) => [m.superseded ? m.id : null, m.role === 'assistant' && m.content?.dashboard?.schema_version === 1 ? m.content.dashboard_revision_of : null])
    .filter(Boolean)), [messages]);
  const questions = useMemo(() => {
    const result = new Map();
    let question;
    for (const message of messages) {
      if (message.role === 'user') question = message.content?.text;
      else result.set(message.id, question);
    }
    return result;
  }, [messages]);
  return (
    <Box sx={{ position: 'relative', flexGrow: 1, minHeight: 0, display: 'flex' }}>
      <Box
        ref={scrollRef} data-chat-scroll="true" role="region" aria-label={translate('assistant.assistantPageConversationMessages')} tabIndex={0} onScroll={onScroll}
        onWheel={(event) => {
          if (event.deltaY < 0) followBottom.current = false;
          if (event.deltaY) {
            const scroller = scrollRef.current;
            updateJump(scroller.scrollHeight - scroller.clientHeight - scroller.scrollTop > BOTTOM_THRESHOLD);
          }
        }}
        sx={{ flexGrow: 1, overflowY: 'auto', minWidth: 0, minHeight: 0, pr: 1, borderRadius: 2, scrollbarGutter: 'stable' }}
      >
        <Box ref={contentRef} sx={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
          {history?.data?.next_before && <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}><Button size="small" disabled={history.isLoadingOlder} onClick={() => loadPage('older')}>{history.isLoadingOlder ? translate('assistant.assistantPageLoadingMessages') : translate('assistant.assistantPageEarlierMessages')}</Button></Box>}
          {historyError && <Alert severity="warning" sx={{ mb: 2 }}>{historyError}</Alert>}
          {children}
          {messages.map((m, i) => (
            <Box key={m.id} data-message-id={m.id} tabIndex={-1} sx={{ '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2, borderRadius: 1 } }}>
              <StoredMessage
                message={m} question={questions.get(m.id)} interactive={!pending && i === lastIndex} onSend={onSend} onReply={onReply}
                answered={answered.has(m.run?.id)} superseded={superseded.has(m.id)}
                filterValues={dashboardValues[m.id]} onFiltersChange={changeFilters}
                replySource={messageById.get(m.content?.reply_to)} onJumpToMessage={jumpToMessage}
              />
            </Box>
          ))}
          {pending && <PendingAnswer live={live} />}
          {!pending && settled && <PendingAnswer live={settled} />}
          {history?.data?.next_after && <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}><Button size="small" disabled={history.isLoadingNewer} onClick={() => loadPage('newer')}>{history.isLoadingNewer ? translate('assistant.assistantPageLoadingMessages') : translate('assistant.assistantPageNextMessages')}</Button></Box>}
        </Box>
      </Box>
      {showJump && (
        <Fade in={jumpVisible} timeout={JUMP_FADE_MS} unmountOnExit>
          <Button
            variant="contained" size="small" startIcon={<ArrowDownwardIcon />} onClick={jumpVisible ? jumpToBottom : undefined}
            aria-label={translate('assistant.assistantPageScrollDown')} aria-hidden={!jumpVisible || undefined} aria-disabled={!jumpVisible || undefined}
            tabIndex={jumpVisible ? 0 : -1}
            sx={{ position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)', borderRadius: 5, boxShadow: 2, zIndex: 1, whiteSpace: 'nowrap', pointerEvents: jumpVisible ? 'auto' : 'none', '@media (prefers-reduced-motion: reduce)': { transition: 'none !important' } }}
          >{translate('assistant.assistantPageScrollDown2')}</Button>
        </Fade>
      )}
    </Box>
  );
});

export default function AssistantPage() {
  const { t: translate } = useTranslation();
  const t = useTokens();
  const qc = useQueryClient();
  const status = useAssistantStatus();
  const [params, setParams] = useSearchParams();
  // Only an id-shaped value ever reaches an API path.
  const requestedId = params.get('c');
  const selectedId = isAssistantId(requestedId) ? requestedId : null;
  const visibleConversation = useRef(selectedId);
  const preserveSkillFor = useRef(null);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [diagnostic, setDiagnostic] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState(undefined);
  const [reply, setReply] = useState(null);
  const [focusToken, setFocusToken] = useState(0);
  const activeReply = reply?.conversationId === selectedId ? reply : null;
  // The picked model and effort: seeded from the server's last pick once per
  // connected provider, then kept as the user switches families in the chat.
  const [choice, setChoice] = useState(null);
  const seededProvider = useRef(null);
  const statusData = status.data;
  useEffect(() => {
    if (!statusData) return;
    const provider = statusData.provider?.id ?? '';
    if (seededProvider.current === provider) return;
    seededProvider.current = provider;
    setChoice(statusData.choice || null);
  }, [statusData]);
  const models = Array.isArray(statusData?.models) ? statusData.models : [];
  // What is sent is what the picker shows: the user's pick while the offer
  // still has it, else the server's last pick (always a valid one).
  const fits = (c) => {
    const model = c && models.find((m) => m.id === c.model);
    return Boolean(model) && (model.efforts.length === 0 ? !c.effort : model.efforts.includes(c.effort));
  };
  const picked = fits(choice) ? choice : statusData?.choice ?? null;
  const [sendError, setSendError] = useState('');
  // The run being answered: {runId, conversationId, userMessage}.
  const [pending, setPending] = useState(null);
  const pendingRef = useRef(pending);
  pendingRef.current = pending;
  const liveRef = useRef(null);
  const [settled, setSettled] = useState(null);
  const [stoppingRunId, setStoppingRunId] = useState(null);
  const submissionRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [restartToken, setRestartToken] = useState(0);
  const finishedRuns = useRef(new Set());
  useEffect(() => {
    let principal = selectPrincipal(useAuthStore.getState());
    const unsubscribe = useAuthStore.subscribe((state) => {
      const next = selectPrincipal(state);
      if (next === principal) return;
      principal = next;
      pendingRef.current = null;
      submissionRef.current?.controller.abort();
      submissionRef.current = null;
      liveRef.current = null;
      finishedRuns.current.clear();
      setPending(null);
      setSettled(null);
      setStoppingRunId(null);
      setSubmitting(false);
      setSendError('');
    });
    return () => {
      unsubscribe();
      submissionRef.current?.controller.abort();
      submissionRef.current = null;
    };
  }, []);

  const enabled = status.isSuccess && status.data?.enabled === true;
  const activeResponse = useActiveRun({ enabled });
  const cancelRun = useCancelRun();
  const cancelResponse = cancelRun.mutateAsync;
  const skillCatalogue = useAssistantSkills({ enabled });
  const skills = skillCatalogue.data || [];
  const conversation = useConversation(enabled ? selectedId : null);
  const loadLatestConversation = conversation.loadLatest;
  const laterMessages = conversation.data?.next_after;
  const createConversation = useCreateConversation();
  const sendMessage = useSendMessage();
  const create = createConversation.mutateAsync;
  const postMessage = sendMessage.mutateAsync;
  const latest = [...(conversation.data?.messages || [])].reverse().find((m) => m.role === 'assistant');
  const replySource = activeReply && conversation.data?.messages?.find((m) => m.id === activeReply.messageId);
  const inherited = activeReply ? (replySource ? replySource.content?.skill?.id : activeReply.skillId) : latest?.content?.skill?.id;
  const skill = skills.find((s) => s.id === (selectedSkill === undefined ? inherited : selectedSkill)) || null;
  useEffect(() => {
    visibleConversation.current = selectedId;
    setReply(null);
    if (preserveSkillFor.current && preserveSkillFor.current === selectedId) {
      preserveSkillFor.current = null;
      return;
    }
    preserveSkillFor.current = null;
    setSelectedSkill(undefined);
  }, [selectedId]);

  const select = useCallback(
    (id) => {
      visibleConversation.current = id;
      setSendError('');
      setReply(null);
      setParams(id ? { c: id } : {}, { replace: false });
    },
    [setParams],
  );
  const chooseReply = useCallback((source) => {
    if (!selectedId || !source?.messageId) return;
    const message = conversation.data?.messages?.find((item) => item.id === source.messageId);
    setReply({ ...source, conversationId: selectedId, skillId: message?.content?.skill?.id || null });
    setFocusToken((current) => current + 1);
    setSendError('');
  }, [selectedId, conversation.data?.messages]);
  const cancelReply = useCallback(() => setReply(null), []);

  const finishRun = useCallback(
    (runId, result = {}) => {
      const current = pendingRef.current;
      if (!current || current.runId !== runId || finishedRuns.current.has(runId)) return;
      if (result.lost && !result.notFound) {
        setSendError(LOST_RUN_TEXT());
        setPending((item) => item?.runId === runId ? { ...item, connectionLost: true } : item);
        qc.invalidateQueries({ queryKey: assistantKeys.activeRun });
        return;
      }
      finishedRuns.current.add(runId);
      if (finishedRuns.current.size > 100) finishedRuns.current.delete(finishedRuns.current.values().next().value);
      if (submissionRef.current?.confirmedRunId === runId) {
        submissionRef.current.controller.abort();
        submissionRef.current = null;
        setSubmitting(false);
      }
      const snapshot = liveRef.current;
      if (snapshot?.text || snapshot?.steps?.length || snapshot?.diagnostics?.length) {
        setSettled({ conversationId: current.conversationId, runId, live: { ...snapshot, status: result.status || 'finished' } });
      }
      // Terminal state must unlock the composer even if a history refresh hangs.
      pendingRef.current = null;
      setPending((item) => item?.runId === runId ? null : item);
      setStoppingRunId((id) => id === runId ? null : id);
      setSendError(result.notFound ? translate('assistant.assistantPageTheAnswerIsUnavailableYouCanSend') : '');
      qc.setQueryData(assistantKeys.activeRun, (data) => data?.run?.id === runId ? { run: null, checkSequence: markRunObservation() } : data);
      const principal = selectPrincipal(useAuthStore.getState());
      Promise.all([
        refreshConversationHistory(qc, current.conversationId),
        qc.invalidateQueries({ queryKey: assistantKeys.conversations }),
      ]).catch(() => {
        if (principal === selectPrincipal(useAuthStore.getState()) && !pendingRef.current && visibleConversation.current === current.conversationId) {
          setSendError(translate('assistant.assistantPageCouldNotRefreshTheHistoryReopenThe'));
        }
      });
    },
    [qc, translate],
  );
  const onFinish = useCallback((result, runId) => finishRun(runId, result), [finishRun]);
  const streamed = useRunEvents(pending?.runId, { onFinish, restartToken });
  const live = pending?.connectionLost ? { ...streamed, status: 'connection_lost' } : streamed;
  liveRef.current = live;

  const stop = useCallback(async () => {
    const current = pendingRef.current;
    if (!current || stoppingRunId === current.runId) return;
    const principal = selectPrincipal(useAuthStore.getState());
    setStoppingRunId(current.runId);
    setSendError('');
    try {
      const result = await cancelResponse(current.runId);
      if (principal !== selectPrincipal(useAuthStore.getState())) return;
      if (FINISHED_RUN_STATUSES.has(result?.run?.status)) finishRun(current.runId, result.run);
      else setSendError(translate('assistant.assistantPageStoppingHasNotBeenConfirmedYetPlease'));
    } catch (error) {
      if (principal !== selectPrincipal(useAuthStore.getState()) || pendingRef.current?.runId !== current.runId) return;
      if (isAssistantNotFound(error)) finishRun(current.runId, { lost: true, notFound: true });
      else setSendError(translate('assistant.assistantPageCouldNotStopTheAnswerCheckYour'));
      qc.invalidateQueries({ queryKey: assistantKeys.activeRun });
    } finally {
      if (principal === selectPrincipal(useAuthStore.getState())) setStoppingRunId((id) => id === current.runId ? null : id);
    }
  }, [cancelResponse, finishRun, stoppingRunId, qc, translate]);

  // The principal-wide endpoint also discovers work started in another tab.
  const serverRun = activeResponse.data?.run;
  useEffect(() => {
    if (!activeResponse.isSuccess) return;
    const current = pendingRef.current;
    if (current && activeResponse.data.checkSequence < (current.acceptedSequence || 0)) return;
    if (serverRun && !finishedRuns.current.has(serverRun.id)) {
      if (submissionRef.current?.conversationId === serverRun.conversation_id) submissionRef.current.confirmedRunId = serverRun.id;
      if (!current || current.runId !== serverRun.id) {
        if (current) finishRun(current.runId);
        setPending({ runId: serverRun.id, conversationId: serverRun.conversation_id, userMessage: null, acceptedSequence: activeResponse.data.checkSequence });
      } else if (current.connectionLost) {
        setSendError('');
        setPending({ ...current, connectionLost: false });
        setRestartToken((value) => value + 1);
      }
    } else if (!serverRun && current && activeResponse.data.checkSequence >= (current.acceptedSequence || 0)) {
      finishRun(current.runId);
    }
  }, [serverRun, activeResponse.data?.checkSequence, activeResponse.isSuccess, finishRun]);

  // A reload mid-answer: pick the run up again from the conversation.
  const activeRun = conversation.data?.active_run;
  useEffect(() => {
    if (pending || !activeRun || !selectedId) return;
    if (finishedRuns.current.has(activeRun.id)) return;
    // An authoritative principal lookup supersedes a stale history snapshot.
    if (activeResponse.isSuccess) return;
    setPending({ runId: activeRun.id, conversationId: selectedId, userMessage: null, acceptedSequence: 0 });
  }, [activeRun, pending, selectedId, activeResponse.isSuccess]);

  const busy = Boolean(pending) || submitting;

  const send = useCallback(async (text, context = {}) => {
    // Without a provider and family nothing is asked (no empty conversation).
    if (busy || submissionRef.current || models.length === 0) return false;
    const originPrincipal = selectPrincipal(useAuthStore.getState());
    const stillCurrent = () => originPrincipal === selectPrincipal(useAuthStore.getState());
    setSendError('');
    let conversationId = selectedId;
    const submission = { controller: new AbortController(), conversationId, confirmedRunId: null };
    submissionRef.current = submission;
    setSubmitting(true);
    try {
      if (!conversationId) {
        const created = await create({ signal: submission.controller.signal });
        if (!stillCurrent()) return false;
        conversationId = created.id;
        submission.conversationId = conversationId;
        // The first conversation keeps the skill picked before it had an id.
        preserveSkillFor.current = conversationId;
        select(conversationId);
      }
      const result = await postMessage({
        conversationId,
        text,
        model: picked?.model,
        effort: picked?.effort,
        skill: context.skill,
        replyTo: context.replyTo,
        quote: context.quote,
        diagnostic,
        signal: submission.controller.signal,
      });
      if (!stillCurrent()) return false;
      const acceptedSequence = markRunObservation();
      const current = pendingRef.current;
      const knownFinished = finishedRuns.current.has(result.run.id);
      const alreadyFinished = knownFinished || FINISHED_RUN_STATUSES.has(result.run.status);
      const newerRun = current && current.runId !== result.run.id;
      const ownsSubmission = submissionRef.current === submission;
      const applyContext = ownsSubmission && !knownFinished && !newerRun;
      if (ownsSubmission && !alreadyFinished && !newerRun) {
        setSettled(null);
        setPending({ runId: result.run.id, conversationId, userMessage: result.message, acceptedSequence });
        qc.setQueryData(assistantKeys.activeRun, { run: { ...result.run, conversation_id: conversationId }, checkSequence: acceptedSequence });
      } else {
        refreshConversationHistory(qc, conversationId).catch(() => {});
        qc.invalidateQueries({ queryKey: assistantKeys.activeRun });
      }
      if (applyContext && visibleConversation.current === conversationId && laterMessages) {
        // History navigation must not delay the stop action for an accepted run.
        loadLatestConversation().catch(() => {
          if (stillCurrent() && visibleConversation.current === conversationId) setSendError(translate('assistant.assistantPageYourQuestionWasSentButRecentMessages'));
        });
      }
      if (applyContext && visibleConversation.current === conversationId) context.onAccepted?.();
      qc.invalidateQueries({ queryKey: assistantKeys.conversations });
      // The accepted pick is the user's last one: a later visit starts from it.
      if (applyContext) qc.setQueryData(assistantKeys.status, (data) => (data && picked ? { ...data, choice: picked } : data));
      return true;
    } catch (err) {
      if (!stillCurrent()) return false;
      if (submission.controller.signal.aborted && submission.confirmedRunId && finishedRuns.current.has(submission.confirmedRunId)) {
        return true;
      }
      const code = assistantErrorCode(err);
      if (code === 'busy') qc.invalidateQueries({ queryKey: assistantKeys.activeRun });
      if (code === 'no_model') setSettingsOpen(true);
      if (['no_model', 'bad_model', 'bad_effort'].includes(code)) {
        // The offer changed under the picker: fetch the current one.
        qc.invalidateQueries({ queryKey: assistantKeys.status });
      }
      setSendError(sendErrorText(err));
      return false;
    } finally {
      if (submissionRef.current === submission) {
        submissionRef.current = null;
        if (stillCurrent()) setSubmitting(false);
      }
    }
  }, [busy, models.length, selectedId, create, postMessage, picked, diagnostic, select, qc, laterMessages, loadLatestConversation, translate]);

  const sendDraft = async (text) => {
    const command = text.trim().split(/\s+/, 1)[0].toLowerCase();
    const commandedSkill = skills.find((s) => s.command.toLowerCase() === command);
    const requestedSkill = commandedSkill || skill;
    if (await send(text, {
      skill: commandedSkill?.id ?? (selectedSkill === null ? null : skill?.id),
      replyTo: activeReply?.messageId || (requestedSkill && inherited === requestedSkill.id ? latest?.id : undefined),
      quote: activeReply?.quote
        ? typeof activeReply.quote === 'string' ? { text: activeReply.quote } : activeReply.quote
        : undefined,
      onAccepted: () => {
        setSelectedSkill(commandedSkill ? commandedSkill.id : selectedSkill);
        setReply((current) => current === activeReply ? null : current);
      },
    })) {
      return true;
    }
    return false;
  };

  const onDeleted = (id) => {
    const current = pendingRef.current;
    const cachedRun = qc.getQueryData(assistantKeys.activeRun)?.run;
    const runId = current?.conversationId === id ? current.runId : cachedRun?.conversation_id === id ? cachedRun.id : null;
    if (runId) {
      finishedRuns.current.add(runId);
      if (finishedRuns.current.size > 100) finishedRuns.current.delete(finishedRuns.current.values().next().value);
    }
    if (submissionRef.current?.confirmedRunId === runId && runId) {
      submissionRef.current.controller.abort();
      submissionRef.current = null;
      setSubmitting(false);
    }
    if (current?.conversationId === id) pendingRef.current = null;
    setPending((item) => item?.conversationId === id ? null : item);
    setSettled((item) => item?.conversationId === id ? null : item);
    setStoppingRunId((value) => value === runId ? null : value);
    qc.setQueryData(assistantKeys.activeRun, (data) => data?.run?.conversation_id === id ? { run: null, checkSequence: markRunObservation() } : data);
    const principal = selectPrincipal(useAuthStore.getState());
    qc.cancelQueries({ queryKey: assistantKeys.activeRun }).then(() => {
      if (principal === selectPrincipal(useAuthStore.getState())) qc.invalidateQueries({ queryKey: assistantKeys.activeRun });
    });
    if (id === selectedId) select(null);
  };

  if (status.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (status.isError) {
    return isAssistantNotFound(status.error) ? (
      <Typography color="text.secondary" sx={{ py: 8, textAlign: 'center' }}>{translate('assistant.assistantPagePageNotFound')}</Typography>
    ) : (
      <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => status.refetch()}>{translate('assistant.answerWidgetRetry')}</Button>}>{translate('assistant.assistantAccessCouldNotLoadTheAssistant')}</Alert>
    );
  }
  if (!enabled) {
    return <Typography color="text.secondary" sx={{ py: 8, textAlign: 'center' }}>{translate('assistant.assistantAccessPageNotFound')}</Typography>;
  }

  if (!enabled) {
    return <Typography color="text.secondary" sx={{ py: 8, textAlign: 'center' }}>{translate('assistant.assistantAccessPageNotFound')}</Typography>;
  }

  const here = pending && pending.conversationId === selectedId ? pending : null;
  const stored = selectedId ? conversation.data?.messages || EMPTY_MESSAGES : EMPTY_MESSAGES;
  const completedLive = settled?.conversationId === selectedId && !stored.some((message) => message.role === 'assistant' && message.run?.id === settled.runId) ? settled.live : null;
  const messages =
    here?.userMessage && !stored.some((m) => m.id === here.userMessage.id)
      ? [...stored, here.userMessage]
      : stored;
  const lostConversation = selectedId && conversation.isError && isAssistantNotFound(conversation.error);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', minHeight: 480 }}>
      <Header status={status.data} onSettings={() => setSettingsOpen(true)} />
      {models.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>{translate('assistant.assistantPageToUseTheAssistantAnOwnerOr')}</Alert>
      )}
      <Box sx={{ display: 'flex', gap: 2, flexGrow: 1, minHeight: 0 }}>
        <Paper variant="outlined" sx={{ width: { xs: 224, md: 288, lg: 304 }, flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: 0, borderRadius: 3, bgcolor: t.canvas, boxShadow: 'none', overflow: 'hidden' }}>
          <ConversationList
            selectedId={selectedId}
            onSelect={select}
            onNew={() => select(null)}
            onDeleted={onDeleted}
          />
        </Paper>
        <Paper variant="outlined" sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column', p: 2, minHeight: 0 }}>
          <Thread key={selectedId || 'new'} messages={messages} pending={here} live={live} settled={completedLive} onSend={send} onReply={chooseReply} history={conversation}>
            {lostConversation && <Alert severity="warning">{translate('assistant.assistantPageConversationNotFoundItMayHaveBeen')}</Alert>}
            {selectedId && conversation.isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            )}
            {selectedId && conversation.isError && !lostConversation && (
              <Alert severity="error">{translate('assistant.assistantPageCouldNotLoadTheConversation')}</Alert>
            )}
            {!here && messages.length === 0 && !(selectedId && (conversation.isLoading || conversation.isError)) && (
              <EmptyState disabled={busy || models.length === 0} onAsk={sendDraft} />
            )}
          </Thread>
          {pending && !here && (
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, pt: 1 }}>
              <Typography variant="caption" sx={{ color: t.inkMuted }}>{translate('assistant.assistantPageAnAnswerIsBeingPreparedInAnother')}</Typography>
              <Button size="small" onClick={() => select(pending.conversationId)}>{translate('assistant.assistantPageOpenConversation')}</Button>
            </Box>
          )}
          <Composer
            key={`composer:${selectedId || 'new'}`}
            onSend={sendDraft}
            disabled={Boolean(lostConversation)}
            busy={busy}
            onStop={pending ? stop : undefined}
            stopping={stoppingRunId === pending?.runId}
            error={sendError}
            models={models}
            families={statusData?.families || []}
            family={statusData?.family}
            choice={picked}
            onChoice={setChoice}
            skills={skills}
            skill={skill}
            onSkill={setSelectedSkill}
            diagnostic={diagnostic}
            onDiagnostic={setDiagnostic}
            reply={activeReply}
            onCancelReply={cancelReply}
            focusToken={focusToken}
          />
        </Paper>
      </Box>
      <SettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </Box>
  );
}
